import { createSelector } from '@reduxjs/toolkit';
import { List as ImList } from 'immutable';

import * as dateSelectors from 'app-components/flux/date/dateSelectors';
import { scheduledTransactionsSelectors, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { taxDueDates } from 'app-components/flux/scheduled-transactions/scheduledTransactionsUtils';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  PaymentStatus,
  TAXES_REDUCER_KEY,
  TaxPlanStats,
  TaxQuarter,
  TaxQuarterProps,
  mkTaxQuarter,
} from './taxesTypes';

type State = Pick<RootState, typeof TAXES_REDUCER_KEY>;

export const getStore = (state: State) => state[TAXES_REDUCER_KEY];

export const getResourcesById = (state: State) => getStore(state).resourcesById;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;
export const getIsProcessing = (state: State) => getStore(state).isProcessing;

export const getTaxPlans = createSelector(getResourcesById, (taxPlans) => {
  const taxPlansList = taxPlans.valueSeq().toList();
  return taxPlansList.sortBy((taxPlan) => taxPlan.input?.year).reverse();
});

export const getTaxPlanById = (state: State, id: string) => getResourcesById(state).get(id);

export const getTaxPlanByYear = createSelector(
  getResourcesById,
  (_state: unknown, year: number) => year,
  (taxPlans, year) => taxPlans?.find((taxPlan) => taxPlan.input?.year === year) || null,
);

export const getCurrentTaxYear = createSelector(dateSelectors.getCurrentDate, (currentDate) => {
  const previous = taxDueDates(currentDate.year - 1);
  return (currentDate?.toISODate() || '') <= previous.q4Due ? currentDate.year - 1 : currentDate.year;
});

const getTransactionPaymentStatusInternal = (
  transaction: transactionsTypes.TransactionRecord | undefined,
  scheduledTransaction: scheduledTransactionsTypes.ScheduledTransaction | undefined,
  currentDate: string,
): PaymentStatus => {
  if (!transaction) {
    return 'SKIPPED';
  } else if (currentDate) {
    if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction.state === 'CLEARED') {
      return 'PAID';
    } else if (!transaction.stDueOn) {
      return 'PENDING';
    } else if (transaction.postedOn && transaction.postedOn < currentDate) {
      return 'OVERDUE';
    } else if (transaction.stDueOn && transaction.stDueOn === currentDate) {
      return 'DUE_TODAY';
    } else if (transaction.stDueOn === scheduledTransaction?.dueOn) {
      return 'UPCOMING';
    }
  }
  return '';
};

export const getTransactionPaymentStatus = createSelector(
  (state, id: string) => transactionsSelectors.getTransactionById(state, id),
  (state, id: string) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(
      state,
      transactionsSelectors.getTransactionById(state, id)?.stModelId || '',
    ),
  dateSelectors.getCurrentDateString,
  getTransactionPaymentStatusInternal,
);

export const getTaxQuarters = createSelector(
  getTaxPlanById,
  (state: any, taxPlanId) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(
      state,
      getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId || '',
    ),
  (state: State, taxPlanId) =>
    transactionsSelectors.getSTModelTransactions(state, getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId),
  dateSelectors.getCurrentDateString,
  (taxPlan, recurrence, transactions, currentDate) => {
    const dueDates = taxDueDates(taxPlan?.input?.year);
    const taxQuarters: TaxQuarterProps[] = [
      {
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q1Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q1',
        startDate: dueDates.q1Start,
        endDate: dueDates.q1End,
      },
      {
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q2Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q2',
        startDate: dueDates.q2Start,
        endDate: dueDates.q2End,
      },
      {
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q3Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q3',
        startDate: dueDates.q3Start,
        endDate: dueDates.q3End,
      },
      {
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q4Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q4',
        startDate: dueDates.q4Start,
        endDate: dueDates.q4End,
      },
    ];
    transactions.forEach((transaction: transactionsTypes.TransactionRecord) => {
      let taxQuarter;
      if (transaction.postedOn <= dueDates.q1Due) {
        taxQuarter = taxQuarters[0];
      } else if (transaction.postedOn > dueDates.q1Due && transaction.postedOn <= dueDates.q2Due) {
        taxQuarter = taxQuarters[1];
      } else if (transaction.postedOn > dueDates.q2Due && transaction.postedOn <= dueDates.q3Due) {
        taxQuarter = taxQuarters[2];
      } else if (transaction.postedOn > dueDates.q3Due) {
        taxQuarter = taxQuarters[3];
      }
      if (taxQuarter) {
        taxQuarter.transactions.push(transaction);
        taxQuarter.amountEstimated += transaction.amount;
        if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction?.state === 'CLEARED') {
          taxQuarter.amountPaid += transaction.amount;
        }
      }
    });
    return ImList(
      taxQuarters.map((taxQuarter) => {
        const transaction =
          taxQuarter.transactions.find((transaction) => !!transaction.stModelId) ||
          (taxQuarter.transactions as transactionsTypes.TransactionRecord[])[0];
        const paymentStatus: PaymentStatus = getTransactionPaymentStatusInternal(transaction, recurrence, currentDate);
        return mkTaxQuarter({
          ...taxQuarter,
          paymentStatus,
          transactions: ImList(taxQuarter.transactions).sortBy((transaction) => transaction.postedOn),
        });
      }),
    );
  },
);

export const getQuarterDue = createSelector(
  getTaxQuarters,
  (state, taxPlanId) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(
      state,
      getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId || '',
    ),
  (taxQuarters, scheduledTransaction) =>
    taxQuarters.find((taxQuarter: TaxQuarter) =>
      taxQuarter.transactions.find(
        (transaction: transactionsTypes.TransactionRecord) => transaction.postedOn === scheduledTransaction?.dueOn,
      ),
    ),
);

export const getQuarterNow = createSelector(
  getTaxQuarters,
  dateSelectors.getCurrentDateString,
  (taxQuarters, currentDate) => {
    if (taxQuarters.size === 4) {
      const q1 = taxQuarters.get(0) as TaxQuarter;
      const q2 = taxQuarters.get(1) as TaxQuarter;
      const q3 = taxQuarters.get(2) as TaxQuarter;
      const q4 = taxQuarters.get(3) as TaxQuarter;
      if (currentDate > q1.startDate && currentDate < q1.dueDate) {
        return q1;
      } else if (currentDate > q1.dueDate && currentDate <= q2.dueDate) {
        return q2;
      } else if (currentDate > q2.dueDate && currentDate <= q3.dueDate) {
        return q3;
      } else if (currentDate > q3.dueDate && currentDate <= q4.dueDate) {
        return q4;
      }
    }
    return undefined;
  },
);

export const getTaxPlanStats = createSelector(getTaxPlanById, getTaxQuarters, (taxPlan, taxQuarters) =>
  taxQuarters.reduce<TaxPlanStats>(
    (stats, taxQuarter) => ({
      ...stats,
      amountPaid: stats.amountPaid + (taxQuarter.amountPaid || 0),
      amountEstimated: stats.amountEstimated + (taxQuarter.amountEstimated || 0),
      ...(!ImList.isList(taxQuarter.transactions)
        ? {}
        : taxQuarter.transactions?.reduce(
            (transactionBuckets: Partial<TaxPlanStats>, transaction: transactionsTypes.TransactionRecord) => {
              if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction?.state === 'CLEARED') {
                transactionBuckets.transactionsPaid?.push(transaction);
              } else {
                transactionBuckets.transactionsUpcoming?.push(transaction);
              }
              return transactionBuckets;
            },
            { transactionsPaid: stats.transactionsPaid, transactionsUpcoming: stats.transactionsUpcoming },
          )),
    }),
    {
      amountPaid: -1 * (taxPlan?.output?.withholdingsAndSummaryCalc?.totalWithholdingsFullYearAndOther || 0), // TODO: QCS flip sign totalWithholdingsFullYearAndOther
      amountEstimated: 0,
      transactionsPaid: [],
      transactionsUpcoming: [],
    },
  ),
);
